// src/pages/services/payroll-compliance.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  ShieldCheck, 
  FileText, 
  IndianRupee, 
  Calendar, 
  Users,
  MapPin 
} from 'lucide-react';

const PayrollCompliance = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 1000], [0, -120]); // stronger parallax for premium feel
  const badgeY = useTransform(scrollY, [0, 600], [0, -40]);

  const handleFreeConsultation = () => navigate('/contact');

  const complianceItems = [
    {
      title: "Provident Fund (PF)",
      desc: "Monthly contribution uploads, ECR filings, PF transfers, UAN management",
      icon: ShieldCheck
    },
    {
      title: "Employees' State Insurance (ESI)",
      desc: "Monthly contributions, coverage verification, dispensary management, claim support",
      icon: Users
    },
    {
      title: "Professional Tax (PT)",
      desc: "State-wise deduction and remittance across applicable states",
      icon: IndianRupee
    },
    {
      title: "Labour Welfare Fund (LWF)",
      desc: "Half-yearly/annual remittances as per state law",
      icon: Calendar
    },
    {
      title: "TDS (Section 192)",
      desc: "Monthly TDS workings, quarterly returns (Form 24Q), Form 16 issuance",
      icon: FileText
    },
    {
      title: "Annual Returns",
      desc: "Form 3A, 6A under PF; Form 5, 6, 7 under ESI",
      icon: FileText
    },
    {
      title: "Coverage Letters",
      desc: "New joinee registration, exit formalities",
      icon: CheckCircle2
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">
      {/* Hero – ultra-premium with enhanced animations */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50 pt-24 overflow-hidden">
        {/* Multiple animated orbs for richer depth */}
        <motion.div
          animate={{ scale: [1, 1.18, 1], rotate: [0, 6, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-60 -left-60 w-[1100px] h-[1100px] bg-emerald-200/25 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.25, 1], rotate: [0, -8, 0] }}
          transition={{ duration: 28, repeat: Infinity }}
          className="absolute -bottom-80 -right-80 w-[1200px] h-[1200px] bg-cyan-200/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [-20, 20, -20], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 12, repeat: Infinity }}
          className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-emerald-300/15 rounded-full blur-2xl"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              style={{ y: badgeY }}
              className="inline-flex items-center gap-4 bg-white/90 backdrop-blur-2xl border border-emerald-100/60 px-9 py-5 rounded-full mb-12 shadow-xl"
            >
              <ShieldCheck className="text-[#d97925]" size={32} />
              <span className="text-[#d97925] font-bold tracking-[0.25em] uppercase text-base">
                Payroll Compliance
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.2 }}
              className="text-6xl md:text-[110px] leading-[0.92] font-black tracking-[-0.05em] mb-12 bg-gradient-to-r from-[#d97925] via-orange-600 to-red-500 bg-clip-text text-transparent"
            >
              Payroll<br />Compliance
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 70 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.4 }}
              className="text-2xl md:text-3xl text-zinc-700 max-w-4xl leading-tight font-light"
            >
              PF, ESI, Professional Tax, LWF, and TDS managed simultaneously — zero penalties and complete audit readiness.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-32 md:py-40 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 xl:gap-24">
            {/* Left – Text & Lists */}
            <div className="lg:col-span-7 space-y-24 lg:space-y-32">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1 }}
              >
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-10 bg-gradient-to-r from-[#d97925] to-red-500 bg-clip-text text-transparent">
                  Overview
                </h2>
                <div className="space-y-8 text-[18px] md:text-[19px] leading-relaxed text-zinc-800">
                  <p>
                    India's payroll compliance landscape is complex and non-negotiable. Organisations must manage PF, ESI, Professional Tax, Labour Welfare Fund, and TDS simultaneously — each with different deadlines, portals, rates, and filing requirements.
                  </p>
                  <p className="font-medium text-zinc-900 text-xl">
                    TickBox automates and manages all statutory payroll-linked compliances, ensuring timely submissions, zero penalties, and complete audit readiness.
                  </p>
                </div>
              </motion.div>

              {/* Compliance Coverage – enhanced cards */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-r from-[#d97925] to-red-500 bg-clip-text text-transparent">
                  Compliance Coverage
                </h3>

                <div className="grid md:grid-cols-2 gap-8">
                  {complianceItems.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 60, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08, type: "spring", stiffness: 80 }}
                      whileHover={{ y: -10, scale: 1.02 }}
                      className="bg-white border border-emerald-100/60 rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-emerald-300 transition-all duration-300"
                    >
                      <div className="flex items-start gap-5">
                        <div className="mt-1 w-10 h-10 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                          <item.icon className="text-[#d97925]" size={24} />
                        </div>
                        <div>
                          <h4 className="text-xl font-semibold mb-3 text-[#d97925]">{item.title}</h4>
                          <p className="text-[17px] leading-relaxed text-zinc-700">{item.desc}</p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right – Visuals with parallax */}
            <motion.div
              style={{ y: imageY }}
              className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-12"
            >
              {/* Hero Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-zinc-200/80">
                <img
                  src="https://images.unsplash.com/photo-1556155099-490a1ba16284?w=1200&auto=format&fit=crop&q=80"
                  alt="Payroll compliance dashboard & team"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-10 left-10 text-white">
                  <p className="text-2xl font-bold drop-shadow-lg">Zero-penalty payroll compliance</p>
                  <p className="text-base opacity-90 mt-2 drop-shadow">PF • ESI • TDS • PT • LWF</p>
                </div>
              </div>

              {/* Accuracy Badge */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-10 border border-emerald-100 shadow-2xl"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-md">
                    <CheckCircle2 className="text-[#d97925]" size={40} />
                  </div>
                  <h4 className="text-3xl font-bold text-[#d97925]">Zero-Penalty Guarantee</h4>
                </div>
                <p className="text-lg text-zinc-800 leading-relaxed">
                  Timely filings, accurate deductions, complete audit trail — never face a single compliance penalty again.
                </p>
              </motion.div>

              {/* Multi-State Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-3xl p-10 border border-emerald-100 shadow-xl flex items-start gap-6"
              >
                <MapPin className="text-[#d97925] mt-1" size={48} />
                <div>
                  <h4 className="text-3xl font-bold mb-4 text-[#d97925]">Multi-State Mastery</h4>
                  <p className="text-lg text-zinc-700 leading-relaxed">
                    State-specific PT, LWF, minimum wage rules — automatically applied and filed correctly every single time.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Final CTA – more dramatic */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1 }}
            className="mt-32 md:mt-40 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-3xl py-24 px-8 md:px-16 text-center shadow-2xl relative overflow-hidden"
          >
            {/* Subtle animated overlay */}
            <motion.div
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-30"
            />

            <h3 className="text-5xl md:text-6xl font-bold tracking-tight mb-8 relative z-10">
              Ready for stress-free payroll compliance?
            </h3>
            <p className="text-2xl md:text-3xl mb-12 max-w-4xl mx-auto opacity-90 relative z-10">
              PF • ESI • TDS • PT • LWF — all handled automatically, filed on time, forever penalty-free.
            </p>

            <motion.button
              whileHover={{ scale: 1.06, boxShadow: "0 30px 60px -15px rgba(16, 185, 129, 0.5)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFreeConsultation}
              className="inline-flex items-center gap-5 px-16 py-8 bg-white text-[#d97925] font-bold text-2xl md:text-3xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-all relative z-10"
            >
              Get Payroll Compliance Assessment
              <ArrowRight size={36} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PayrollCompliance;